import { Box, Typography, useMediaQuery, Grid, TextField, Button, Checkbox, FormControlLabel, Select, MenuItem } from "@mui/material";
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import bannerProd from '../../assets/image/landing/banner-prod.png';
import collaborationImg from '../../assets/image/landing/contact.jpg';

const ContactUsSectionComponent = () => {
    const isSmallScreen = useMediaQuery('(max-width:900px)');
    const { enqueueSnackbar } = useSnackbar();

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        company: '',
        jobTitle: '',
        email: '',
        mobile: '',
        country: '',
        purpose: '',
        message: '',
        privacyAgreement: false
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async () => {
        // Basic Validation
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.mobile || !formData.country || !formData.purpose || !formData.privacyAgreement) {
            enqueueSnackbar('Please fill in all required fields and agree to the privacy notice.', { variant: 'warning' });
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch("https://formsubmit.co/ajax/radix_info@radigroup.co", {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    ...formData,
                    _subject: `New Inquiry from ${formData.firstName} ${formData.lastName}`,
                    _template: 'table'
                })
            });

            const result = await response.json();

            if (response.ok) {
                enqueueSnackbar('Message sent successfully! We will get back to you soon.', { variant: 'success' });
                setFormData({
                    firstName: '',
                    lastName: '',
                    company: '',
                    jobTitle: '',
                    email: '',
                    mobile: '',
                    country: '',
                    purpose: '',
                    message: '',
                    privacyAgreement: false
                });
            } else {
                enqueueSnackbar(result.message || 'Failed to send message. Please try again.', { variant: 'error' });
            }
        } catch (error) {
            console.error('Email submission error:', error);
            enqueueSnackbar('An error occurred. Please try again later.', { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isValidMobile = (mobile) => /^[0-9+\-\s]+$/.test(mobile) && mobile.length >= 7;

    const isFormValid = formData.firstName &&
        formData.lastName &&
        formData.email && isValidEmail(formData.email) &&
        formData.mobile && isValidMobile(formData.mobile) &&
        formData.country &&
        formData.purpose &&
        formData.privacyAgreement;

    return (
        <Box
            id="contact-us"
            sx={{
                position: 'relative',
                minHeight: 800,
                display: 'flex',
                alignItems: 'center',
                color: '#f2f2f2',
                overflow: 'hidden'
            }}
        >
            {/* Background Image with Overlay */}
            <Box
                component="img"
                src={bannerProd}
                alt="Background"
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: 0
                }}
            />
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(8, 36, 77, 0.85)', // Strong blue overlay
                zIndex: 1
            }} />

            <Grid container spacing={4} sx={{ position: 'relative', zIndex: 2, px: isSmallScreen ? 2 : 10, py: 8 }}>

                {/* Left Column: Text & Team Image */}
                <Grid item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2, color: '#fff', textAlign: 'start' }}> {/* Gold Accent */}
                        Contact Us
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontSize: '1.1rem', maxWidth: 500, textAlign: 'start' }}>
                        At Radix, we're committed to providing customized defense and security solutions that meet your unique needs. Whether you're looking to upgrade infrastructure, enhance cybersecurity, or streamline operations, our team is here to assist you every step of the way.
                        <br /><br />
                        Reach out to us today, and let us help you build a safer, more secure future with our advanced, integrated systems.
                    </Typography>

                    {/* Circular/Arched Team Image Overlay */}
                    <Box
                        sx={{
                            position: 'relative',
                            height: 300,
                            width: '100%',
                            maxWidth: 400,
                            overflow: 'hidden',
                            borderRadius: '200px 200px 0 0', // Arch shape
                            border: '4px solid rgba(255,255,255,0.2)',
                            mt: 4
                        }}
                    >
                        <Box
                            component="img"
                            src={collaborationImg}
                            alt="Radix Team"
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                    </Box>
                </Grid>

                {/* Right Column: Inquiry Form Card */}
                <Grid item xs={12} md={7}>
                    <Box sx={{
                        bgcolor: '#fff',
                        borderRadius: 2,
                        p: 4,
                        color: '#333',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                    }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#08244d' }}>
                            Let's Talk
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
                            Ready to transform your operations with smarter security and infrastructure solutions?
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="First Name *"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Last Name *"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Company"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleChange}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Job Title"
                                    name="jobTitle"
                                    value={formData.jobTitle}
                                    onChange={handleChange}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Email Address *"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    variant="outlined"
                                    size="small"
                                    error={formData.email !== '' && !isValidEmail(formData.email)}
                                    helperText={formData.email !== '' && !isValidEmail(formData.email) ? "Invalid email address" : ""}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Mobile Number *"
                                    name="mobile"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    variant="outlined"
                                    size="small"
                                    error={formData.mobile !== '' && !isValidMobile(formData.mobile)}
                                    helperText={formData.mobile !== '' && !isValidMobile(formData.mobile) ? "Must contain only numbers" : ""}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Select
                                    fullWidth
                                    displayEmpty
                                    name="country"
                                    value={formData.country}
                                    onChange={handleChange}
                                    size="small"
                                >
                                    <MenuItem value="" disabled>Country *</MenuItem>
                                    <MenuItem value="PH">Philippines</MenuItem>
                                    <MenuItem value="US">USA</MenuItem>
                                </Select>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Select
                                    fullWidth
                                    displayEmpty
                                    name="purpose"
                                    value={formData.purpose}
                                    onChange={handleChange}
                                    size="small"
                                >
                                    <MenuItem value="" disabled>Purpose *</MenuItem>
                                    <MenuItem value="Inquiry">General Inquiry</MenuItem>
                                    <MenuItem value="Support">Support</MenuItem>
                                    <MenuItem value="Sales">Sales</MenuItem>
                                </Select>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    multiline
                                    rows={4}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="privacyAgreement"
                                            checked={formData.privacyAgreement}
                                            onChange={handleChange}
                                        />
                                    }
                                    label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>By ticking the box, I agree that I have read the <span style={{ color: 'blue' }}>Data Privacy Notice</span>*</Typography>}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting || !isFormValid}
                                    sx={{
                                        bgcolor: '#08244d',
                                        color: '#fff',
                                        fontWeight: 'bold',
                                        py: 1.5,
                                        '&:hover': { bgcolor: '#08244d' }
                                    }}
                                >
                                    {isSubmitting ? 'SENDING...' : 'SUBMIT'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default ContactUsSectionComponent;
